import { useEffect, useRef } from "react";
import { format, isPast, isToday, isTomorrow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Task, TaskLabel, TaskStatus, TaskPriority } from "@/types/task-management";
import { mockTaskLabels } from "@/lib/mock/task-management";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Calendar, 
  Flag, 
  Tag, 
  Briefcase, 
  Building2,
  CheckCircle2,
  Sun,
  SunDim,
  GripVertical,
  MoreVertical,
  Pencil,
  Trash2,
  ClipboardList,
  ArrowRight
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import confetti from 'canvas-confetti';

interface TaskCardProps {
  task: Task;
  onClick: () => void;
  onAddToMyDay?: (added: boolean) => void;
  onStatusChange?: (status: TaskStatus) => void;
  onPriorityChange?: (priority: TaskPriority) => void;
  onDelete?: () => void;
  onPostpone?: () => void;
  isCompact?: boolean;
}

export default function TaskCard({ 
  task, 
  onClick, 
  onAddToMyDay, 
  onStatusChange,
  onPriorityChange,
  onDelete,
  onPostpone,
  isCompact = false 
}: TaskCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const prevStatusRef = useRef(task.status);
  
  // Função para disparar o efeito de confete
  const triggerConfetti = () => {
    if (!cardRef.current) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;
    
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { x, y },
      colors: ['#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800'],
      zIndex: 9999,
    });
  };
  
  // Verificar quando o status muda para "completed"
  useEffect(() => {
    if (task.status === "completed" && prevStatusRef.current !== "completed") {
      setTimeout(() => triggerConfetti(), 100);
    }
    prevStatusRef.current = task.status;
  }, [task.status]);
  
  // Manipulador para alteração de status
  const handleStatusChange = (status: TaskStatus) => {
    if (onStatusChange) {
      onStatusChange(status);
    }
  };
  
  // Formatar data de vencimento
  const formatDueDate = (dateString?: string) => {
    if (!dateString) return null;
    
    const date = new Date(dateString);
    
    if (isToday(date)) {
      return "Hoje";
    } else if (isTomorrow(date)) {
      return "Amanhã";
    } else {
      return format(date, "dd 'de' MMMM", { locale: ptBR });
    }
  };
  
  // Verificar se a tarefa está atrasada
  const isOverdue = task.due_date ? isPast(new Date(task.due_date)) && task.status !== "completed" : false;
  
  // Obter labels da tarefa
  const taskLabels = task.labels
    .map(labelId => mockTaskLabels.find(label => label.id === labelId))
    .filter(label => label !== undefined) as TaskLabel[];
  
  // Determinar a cor da prioridade
  const getPriorityColor = () => {
    switch (task.priority) {
      case "urgent": return "text-red-600";
      case "high": return "text-orange-500";
      case "medium": return "text-yellow-500";
      case "low": return "text-blue-500";
      default: return "text-gray-500";
    }
  };
  
  // Determinar o texto da prioridade
  const getPriorityText = () => {
    switch (task.priority) {
      case "urgent": return "Urgente";
      case "high": return "Alta";
      case "medium": return "Média";
      case "low": return "Baixa";
      default: return "Sem prioridade";
    }
  };
  
  // Determinar a cor da borda com base na prioridade
  const getPriorityBorderColor = () => {
    switch (task.priority) {
      case "urgent": return "border-t-red-600";
      case "high": return "border-t-orange-500";
      case "medium": return "border-t-yellow-500";
      case "low": return "border-t-blue-500";
      default: return "border-t-gray-300";
    }
  };

  // Determinar a cor de fundo com base na prioridade (para o modo compacto)
  const getPriorityBgColor = () => {
    switch (task.priority) {
      case "urgent": return "bg-red-50";
      case "high": return "bg-orange-50";
      case "medium": return "bg-yellow-50";
      case "low": return "bg-blue-50";
      default: return "bg-gray-50";
    }
  };

  // Obter o texto do status
  const getStatusText = (status: TaskStatus) => {
    switch (status) {
      case "todo": return "A fazer";
      case "in_progress": return "Em andamento";
      case "waiting": return "Aguardando";
      case "completed": return "Concluída";
      case "cancelled": return "Cancelada";
      default: return status;
    }
  };
  
  return (
    <div 
      ref={cardRef}
      className={`
        border rounded-lg mb-2 hover:bg-gray-50 transition-colors cursor-pointer border-t-4 
        ${getPriorityBorderColor()} 
        ${task.status === "completed" ? "bg-gray-50" : ""}
        ${isCompact ? "p-2" : "p-3"}
        group relative w-full
      `}
      onClick={onClick}
    >
      {/* Indicador de arrastar (drag handle) */}
      <div className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>

      {/* Menu de contexto */}
      <div className="absolute right-1 top-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => e.stopPropagation()}>
              <MoreVertical className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem 
              className="flex items-center gap-2 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
            >
              <Pencil className="h-4 w-4" />
              <span>Editar tarefa</span>
            </DropdownMenuItem>

            {onAddToMyDay && (
              <DropdownMenuItem 
                className={`flex items-center gap-2 cursor-pointer ${task.added_to_my_day ? "bg-amber-50" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToMyDay(!task.added_to_my_day);
                }}
              >
                {task.added_to_my_day ? (
                  <Sun className="h-4 w-4 text-amber-500" />
                ) : (
                  <SunDim className="h-4 w-4 text-gray-500" />
                )}
                <span>{task.added_to_my_day ? "Remover do meu dia" : "Adicionar ao meu dia"}</span>
              </DropdownMenuItem>
            )}

            {onStatusChange && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="flex items-center gap-2">
                  <ArrowRight className="h-4 w-4" />
                  <span>Mover para</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem 
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onStatusChange("todo");
                      }}
                      disabled={task.status === "todo"}
                    >
                      <div className="w-2 h-2 rounded-full bg-blue-600" />
                      <span>A fazer</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onStatusChange("in_progress");
                      }}
                      disabled={task.status === "in_progress"}
                    >
                      <div className="w-2 h-2 rounded-full bg-amber-600" />
                      <span>Em andamento</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onStatusChange("waiting");
                      }}
                      disabled={task.status === "waiting"}
                    >
                      <div className="w-2 h-2 rounded-full bg-purple-600" />
                      <span>Aguardando</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onStatusChange("completed");
                      }}
                      disabled={task.status === "completed"}
                    >
                      <div className="w-2 h-2 rounded-full bg-green-600" />
                      <span>Concluída</span>
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )}

            {onPriorityChange && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="flex items-center gap-2">
                  <Flag className="h-4 w-4" />
                  <span>Prioridade</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem 
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPriorityChange("urgent");
                      }}
                      disabled={task.priority === "urgent"}
                    >
                      <div className="w-2 h-2 rounded-full bg-red-600" />
                      <span>Urgente</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPriorityChange("high");
                      }}
                      disabled={task.priority === "high"}
                    >
                      <div className="w-2 h-2 rounded-full bg-orange-500" />
                      <span>Alta</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPriorityChange("medium");
                      }}
                      disabled={task.priority === "medium"}
                    >
                      <div className="w-2 h-2 rounded-full bg-yellow-500" />
                      <span>Média</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPriorityChange("low");
                      }}
                      disabled={task.priority === "low"}
                    >
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      <span>Baixa</span>
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )}

            {onPostpone && (
              <DropdownMenuItem 
                className="flex items-center gap-2 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onPostpone();
                }}
              >
                <ArrowRight className="h-4 w-4" />
                <span>Prorrogar tarefa</span>
              </DropdownMenuItem>
            )}

            {onDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="flex items-center gap-2 cursor-pointer text-red-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm("Tem certeza que deseja excluir esta tarefa?")) {
                      onDelete();
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Excluir tarefa</span>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className={`flex flex-col gap-${isCompact ? "1" : "2"} pl-3`}>
        {/* Título e badges */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            {task.status === "completed" && (
              <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
            )}
            <h3 className={`font-extrabold ${task.status === "completed" ? "line-through text-gray-500" : ""} ${isCompact ? "text-sm" : ""}`}>
              {task.title}
            </h3>
          </div>
          
          {/* Badges para playbook e cliente - apenas no modo normal */}
          {!isCompact && !task.is_personal && (
            <div className="flex flex-wrap gap-1 mt-1">
              {task.playbook_name && (
                <Badge variant="outline" className="flex items-center gap-1 text-xs">
                  <Briefcase className="h-3 w-3" />
                  <span className="truncate max-w-[100px]">{task.playbook_name}</span>
                </Badge>
              )}
              
              {task.client_name && (
                <Badge variant="outline" className="flex items-center gap-1 text-xs">
                  <Building2 className="h-3 w-3" />
                  <span className="truncate max-w-[100px]">{task.client_name}</span>
                </Badge>
              )}
            </div>
          )}
        </div>
        
        {/* Descrição (se houver) - apenas no modo normal */}
        {!isCompact && task.description && (
          <p className="text-sm text-gray-600 line-clamp-2">{task.description}</p>
        )}
        
        {/* Labels - no modo compacto, mostrar apenas os primeiros 2 */}
        {taskLabels.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {(isCompact ? taskLabels.slice(0, 2) : taskLabels).map(label => (
              <span 
                key={label.id} 
                className={`inline-block px-1.5 py-0.5 rounded-sm ${isCompact ? "text-[10px]" : "text-xs"}`}
                style={{ backgroundColor: `${label.color}20`, color: label.color }}
              >
                {label.name}
              </span>
            ))}
            {isCompact && taskLabels.length > 2 && (
              <span className="inline-block px-1.5 py-0.5 rounded-sm text-[10px] bg-gray-100 text-gray-600">
                +{taskLabels.length - 2}
              </span>
            )}
          </div>
        )}
        
        {/* Rodapé com data e responsável */}
        <div className="flex justify-between items-center mt-1">
          {/* Data de vencimento */}
          {task.due_date && (
            <div className={`flex items-center gap-1 ${isCompact ? "text-[10px]" : "text-xs"} ${isOverdue ? "text-red-600 font-extrabold" : "text-gray-500"}`}>
              <Calendar className={`${isCompact ? "h-2.5 w-2.5" : "h-3 w-3"}`} />
              <span>{formatDueDate(task.due_date)}</span>
            </div>
          )}
          
          <div className="flex items-center gap-1 ml-auto">
            {/* Botão "Adicionar ao meu dia" */}
            {onAddToMyDay && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant={task.added_to_my_day ? "secondary" : "ghost"}
                      size="icon" 
                      className={`${isCompact ? "h-5 w-5" : "h-6 w-6"} ${task.added_to_my_day ? "bg-amber-100" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddToMyDay(!task.added_to_my_day);
                      }}
                    >
                      {task.added_to_my_day ? (
                        <Sun className={`${isCompact ? "h-2.5 w-2.5" : "h-3 w-3"} text-amber-500`} />
                      ) : (
                        <SunDim className={`${isCompact ? "h-2.5 w-2.5" : "h-3 w-3"} text-gray-500`} />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {task.added_to_my_day ? "Remover do meu dia" : "Adicionar ao meu dia"}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            
            {/* Botão "Prorrogar tarefa" */}
            {onPostpone && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className={`${isCompact ? "h-5 w-5" : "h-6 w-6"}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPostpone();
                      }}
                    >
                      <ArrowRight className={`${isCompact ? "h-2.5 w-2.5" : "h-3 w-3"} text-gray-500`} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Prorrogar tarefa
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            
            {/* Avatar do responsável */}
            {task.assignee_name && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Avatar className={`${isCompact ? "h-5 w-5" : "h-6 w-6"}`}>
                      <AvatarImage src={task.assignee_avatar} alt={task.assignee_name} />
                      <AvatarFallback>{task.assignee_name.charAt(0)}</AvatarFallback>
                    </Avatar>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Responsável: {task.assignee_name}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
        
        {/* Subtarefas - apenas no modo normal */}
        {!isCompact && task.subtasks_count && task.subtasks_count > 0 && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <span>
              {task.completed_subtasks_count || 0}/{task.subtasks_count} subtarefas
            </span>
          </div>
        )}
      </div>
    </div>
  );
} 